import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';

export interface ArLineItem {
  description: string;
  qty: number;
  unitPriceCents: number;
  taxRate: number;
}

export interface ArInvoice {
  id: string;
  workspace_id: string;
  entity_id: string | null;
  customer_id: string | null;
  status: 'draft' | 'sent' | 'partial' | 'paid' | 'overdue' | 'void';
  invoice_number: string | null;
  issue_date: string | null;
  due_date: string | null;
  currency: string;
  subtotal_cents: string;
  tax_cents: string;
  total_cents: string;
  paid_cents: string;
  jp_qualified: boolean;
  line_items: ArLineItem[];
  pdf_url: string | null;
  peppol_sent_at: string | null;
  peppol_message_id: string | null;
  owner_user_id: string | null;
  created_at: string;
  updated_at: string;
}

export function useArInvoices(wsId: string | undefined, status?: string) {
  return useQuery<ArInvoice[]>({
    queryKey: ['ar-invoices', wsId, status ?? ''],
    enabled: !!wsId,
    queryFn: async () => {
      const params = new URLSearchParams();
      if (status) params.set('status', status);
      const r = await api.get<ArInvoice[]>(`/workspaces/${wsId}/ar-invoices?${params.toString()}`);
      return r.data;
    },
  });
}

export function useArInvoice(wsId: string | undefined, id: string | null | undefined) {
  return useQuery<ArInvoice>({
    queryKey: ['ar-invoice', wsId, id],
    enabled: !!wsId && !!id,
    queryFn: async () => (await api.get<ArInvoice>(`/workspaces/${wsId}/ar-invoices/${id}`)).data,
  });
}

export interface ArInvoiceCreate {
  invoiceNumber: string;
  issueDate: string;
  dueDate: string;
  currency: string;
  lineItems: ArLineItem[];
  jpQualified?: boolean;
  customerId?: string;
  entityId?: string;
}

export function useCreateArInvoice(wsId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: ArInvoiceCreate) => {
      const r = await api.post<ArInvoice>(`/workspaces/${wsId}/ar-invoices`, body);
      return r.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ar-invoices', wsId] });
    },
  });
}

export function useUploadArInvoice(wsId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (file: File) => {
      const form = new FormData();
      form.append('file', file);
      const r = await api.post<ArInvoice>(`/workspaces/${wsId}/ar-invoices/upload`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return r.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ar-invoices', wsId] });
    },
  });
}

export async function openArInvoiceFile(wsId: string, id: string): Promise<void> {
  const r = await api.get(`/workspaces/${wsId}/ar-invoices/${id}/file`, {
    responseType: 'blob',
  });
  const url = URL.createObjectURL(r.data as Blob);
  window.open(url, '_blank', 'noopener,noreferrer');
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

export function useSendArInvoice(wsId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      (await api.post<ArInvoice>(`/workspaces/${wsId}/ar-invoices/${id}/send`)).data,
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: ['ar-invoices', wsId] });
      qc.invalidateQueries({ queryKey: ['ar-invoice', wsId, id] });
    },
  });
}

/** Generate (or regenerate) the compliant 適格請求書 PDF, then open it. */
export function useGenerateArPdf(wsId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const r = await api.post<ArInvoice>(`/workspaces/${wsId}/ar-invoices/${id}/pdf`);
      return r.data;
    },
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: ['ar-invoices', wsId] });
      qc.invalidateQueries({ queryKey: ['ar-invoice', wsId, id] });
    },
  });
}

export function useVoidArInvoice(wsId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => (await api.post<ArInvoice>(`/workspaces/${wsId}/ar-invoices/${id}/void`)).data,
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: ['ar-invoices', wsId] });
      qc.invalidateQueries({ queryKey: ['ar-invoice', wsId, id] });
    },
  });
}

export function useRecordArPayment(wsId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, amountCents }: { id: string; amountCents: number }) =>
      (await api.post<ArInvoice>(`/workspaces/${wsId}/ar-invoices/${id}/payment`, { amountCents })).data,
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: ['ar-invoices', wsId] });
      qc.invalidateQueries({ queryKey: ['ar-invoice', wsId, id] });
    },
  });
}
